import { Badge, Button, Table } from "@jhool-io/fe-components";
import { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
    cn,
    convertTimeTo12HoursFormat,
    displayNameInRightFormat,
    formatDate,
    handleDisplayClientEmailCorrectly,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../../../utils/helpers";
import {
    IProviderCaseloadDashboard,
    ISuperviseeCaseload,
} from "../../../../../providers/types/providers.types";
import ListState from "../../../../../../components/ListState/ListState";
import ChevronUpIcon from "../../../../../../icons/ChevronUp";
import ChevronDownIcon from "../../../../../../icons/ChevronDown";

interface SuperviseeCaseloadItemProps {
    isOpen?: boolean;
    details: ISuperviseeCaseload;
}

const columns: ColumnDef<IProviderCaseloadDashboard>[] = [
    {
        accessorKey: "client",
        header: "CLIENT NAME",
        cell: ({ row }) =>
            displayNameInRightFormat({
                firstName: row.original.first_name,
                lastName: row.original.last_name,
            }),
    },

    {
        accessorKey: "email",
        header: "EMAIL",
        cell: ({ row }) =>
            handleDisplayClientEmailCorrectly(row.original.email),
    },
    {
        accessorKey: "client_id",
        header: "SESSION TYPE",
        cell: () => "Teletherapy",
    },
    {
        accessorKey: "session_type",
        header: "APPOINTMENT",
        cell: ({ row }) =>
            makeStringFirstLetterCapital(
                removeEnumUnderscore(row.original.session_type)
            ),
    },
    {
        accessorKey: "recurrence_rule",
        header: "APPOINTMENT",
        cell: ({ row }) =>
            `${row.original.session_day}, ${convertTimeTo12HoursFormat(
                `${row.original.session_start_hr}:${row.original.session_start_min}`
            )} ${makeStringFirstLetterCapital(row.original.recurrence_rule)}`,
    },
    {
        accessorKey: "client_id",
        header: "starting on",
        cell: ({ row }) =>
            row.original.first_session_date
                ? formatDate(row.original.first_session_date)
                : "--",
    },
    {
        accessorKey: "",
        header: "status",
        cell: ({ row }) => (
            <div className="flex item-center gap-x-8">
                {row.original.accepts_assignment ? (
                    <Badge content="Accepting A" type="success" />
                ) : null}
                {row.original.pending_supervisor_intake_note_task ? (
                    <Badge content="Intake note" type="neutral" />
                ) : (
                    <Badge content="No Intake note" type="danger" />
                )}
            </div>
        ),
    },
];

export default function SuperviseeCaseloadItem({
    isOpen,
    details,
}: SuperviseeCaseloadItemProps) {
    const [innerIsOpen, setInnerIsOpen] = useState(() => isOpen || false);

    const formattedData: IProviderCaseloadDashboard[] = [];

    details.client_appointments?.forEach((item) => {
        const { client } = item;
        item.appointments.forEach((innerItem) => {
            formattedData.push({
                ...client,
                ...innerItem,
            });
        });
    });

    return (
        <div className="rounded-md border border-strokedark bg-[#E8F1F4] text-black">
            <Button
                variant="normal"
                size="auto"
                className={cn(
                    "w-full flex items-center justify-between px-[--padding-card-x] py-[--padding-card-y] gap-x-2 transition-all hover:brightness-90"
                )}
                onClick={() => setInnerIsOpen(!innerIsOpen)}
            >
                <div className="flex items-start flex-1 flex-wrap gap-[--spacing-items] text-dark">
                    <div className="flex flex-col gap-y-[2px] text-left">
                        <span className="text-caption font-medium tracking-[-0.084px]">
                            {displayNameInRightFormat({
                                firstName: details.first_name,
                                lastName: details.last_name,
                            })}
                        </span>
                        <span className="opacity-50 text-caption font-normal leading-16">
                            {details.email}
                        </span>
                    </div>
                    <span className="opacity-50 text-caption uppercase font-normal">
                        {details.credential || "--"}
                    </span>
                    <div>
                        <span className="text-caption font-medium inline-flex gap-x-[2px]">
                            PHONE NO:
                            <span className="opacity-50 font-normal">
                                {details.contact}
                            </span>
                        </span>
                    </div>
                    <Badge
                        content={`${details.active_clients} Clients`}
                        type="success"
                    />
                    <Badge
                        content={`${details.pending_notes} Pending Notes`}
                        type="warning"
                    />
                </div>
                {innerIsOpen ? (
                    <ChevronUpIcon className="stroke-dark size-4" />
                ) : (
                    <ChevronDownIcon className="stroke-dark size-4" />
                )}
            </Button>
            {innerIsOpen ? (
                <div
                    className={cn(
                        "overflow-hidden transition-all mx-1 mb-[9px] bg-white",
                        {
                            "animate-accordion-down": innerIsOpen,
                            "animate-accordion-up": !innerIsOpen,
                        }
                    )}
                >
                    {formattedData.length === 0 ? (
                        <ListState
                            stateHelperText="No caseload to display"
                            isError={false}
                            emptyMessage="No caseload"
                            context="general"
                        />
                    ) : null}
                    {formattedData.length > 0 ? (
                        <div>
                            <Table columns={columns} data={formattedData} />
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
